import * as React from 'react';
import { useState, useEffect, FC, ReactNode } from 'react';
import { Box, Typography, GlobalStyles } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// ─── Brand tokens (identical to Landing.tsx) ──────────────────────────────────
const BRAND = {
    purple:       '#7B5EA7',
    purpleLight:  '#9B7EC8',
    purpleDark:   '#3D2A6B',
    purpleDeep:   '#1A0F3D',
    lavenderSoft: '#F7F4FC',
    accent:       '#C8B8E8',
    ink:          '#0D0A1A',
    cream:        '#FAF9FC',
    muted:        '#6B5F85',
    border:       '#E4DDF2',
} as const;

type Tab = 'terms' | 'privacy';

// ─── Global styles ────────────────────────────────────────────────────────────
const LegalGlobalStyles: React.FC = () => (
    <GlobalStyles styles={`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .rv {
            opacity: 0;
            transform: translateY(28px);
            transition: opacity .75s cubic-bezier(.16,1,.3,1), transform .75s cubic-bezier(.16,1,.3,1);
        }
        .rv.vis { opacity: 1; transform: none; }
        .rv.d1 { transition-delay: .08s; }
        .rv.d2 { transition-delay: .16s; }
        .rv.d3 { transition-delay: .24s; }
        .rv.d4 { transition-delay: .32s; }
    `} />
);

// ─── useReveal ────────────────────────────────────────────────────────────────
const useReveal = (activeTab: Tab): void => {
    useEffect(() => {
        (window as any).__rvIO?.disconnect();

        const panelId = activeTab === 'terms' ? 'legal-terms' : 'legal-privacy';
        const panel   = document.getElementById(panelId);
        panel?.querySelectorAll<HTMLElement>('.rv').forEach((el) => el.classList.remove('vis'));

        const raf = requestAnimationFrame(() => {
            const io = new IntersectionObserver(
                (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('vis'); }),
                { threshold: 0.08 },
            );
            panel?.querySelectorAll<HTMLElement>('.rv').forEach((el) => io.observe(el));
            (window as any).__rvIO = io;
        });

        return () => cancelAnimationFrame(raf);
    }, [activeTab]);
};

// ─── Logo (matches Landing.tsx) ───────────────────────────────────────────────
const Logo: React.FC<{ size?: number }> = ({ size = 36 }) => (
    <Box
        component="img"
        src="images/ngc.png"
        alt="NG Credit"
        sx={{ width: size, height: size, borderRadius: `${size * 0.18}px`, display: 'block', flexShrink: 0 }}
    />
);

// ─── NAV ──────────────────────────────────────────────────────────────────────
interface NavProps { activeTab: Tab; setActiveTab: (t: Tab) => void; }

const Nav: FC<NavProps> = ({ activeTab, setActiveTab }) => {
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    return (
        <Box
            component="nav"
            sx={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                px: { xs: 3, md: 6 }, height: 68,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: scrolled ? alpha(BRAND.cream, 0.96) : BRAND.cream,
                borderBottom: `1px solid ${scrolled ? BRAND.border : 'transparent'}`,
                backdropFilter: scrolled ? 'blur(16px)' : 'none',
                transition: 'background .35s, border-color .35s, backdrop-filter .35s',
            }}
        >
            {/* Logo — clicks back to landing */}
            <Box
                onClick={() => navigate('/')}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
            >
                <Logo size={34} />
                <Typography sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.25rem', color: BRAND.purpleDark, letterSpacing: '-.01em' }}>
                    NG Credit
                </Typography>
            </Box>

            {/* Tab switcher */}
            <Box sx={{ display: 'flex', gap: '4px', background: BRAND.lavenderSoft, p: '4px', borderRadius: 100, border: `1px solid ${BRAND.border}`, mx: { xs: 2, md: 0 } }}>
                {([['terms', 'Terms of Service'], ['privacy', 'Privacy Policy']] as [Tab, string][]).map(([id, label]) => (
                    <Box
                        key={id}
                        component="button"
                        onClick={() => { setActiveTab(id); window.scrollTo({ top: 0 }); }}
                        sx={{
                            background: activeTab === id ? BRAND.purple : 'transparent',
                            color: activeTab === id ? '#fff' : BRAND.muted,
                            border: 'none', cursor: 'pointer',
                            px: { xs: 1.5, md: 2.5 }, py: .9, borderRadius: 100,
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: { xs: '.72rem', md: '.8rem' }, fontWeight: 600, letterSpacing: '.01em',
                            whiteSpace: 'nowrap',
                            transition: 'background .25s, color .25s',
                        }}
                    >{label}</Box>
                ))}
            </Box>

            {/* Badge — hidden on mobile */}
            <Typography sx={{ display: { xs: 'none', md: 'block' }, fontSize: '.72rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: BRAND.muted, border: `1px solid ${BRAND.border}`, px: 1.75, py: .75, borderRadius: 100 }}>
                Legal Centre
            </Typography>
        </Box>
    );
};

// ─── HERO ─────────────────────────────────────────────────────────────────────
const Hero: FC<{ activeTab: Tab }> = ({ activeTab }) => (
    <Box sx={{
        background: `linear-gradient(160deg, ${BRAND.purpleDeep} 0%, ${BRAND.purpleDark} 60%, ${BRAND.purple} 100%)`,
        pt: '120px', pb: 8, px: { xs: 3, md: 6 },
        position: 'relative', overflow: 'hidden',
    }}>
        <Box sx={{ position: 'absolute', top: -80, right: -60, width: 440, height: 440, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND.accent, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 740, mx: 'auto', position: 'relative' }}>
            <Typography sx={{ fontSize: '.7rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: BRAND.accent, mb: 1.75 }}>
                Stor Technologies Ltd · NG Credit
            </Typography>
            <Typography variant="h1" sx={{ fontFamily: "'Instrument Serif', serif", fontSize: { xs: '2rem', md: '3.5rem' }, fontWeight: 400, color: '#fff', lineHeight: 1.1, mb: 1.75 }}>
                {activeTab === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            </Typography>
            <Typography sx={{ fontSize: '.95rem', color: alpha('#fff', .55), lineHeight: 1.75, maxWidth: 480 }}>
                {activeTab === 'terms'
                    ? 'The agreement governing your use of the NG Credit platform, operated by Stor Technologies Ltd under Nigerian law.'
                    : 'How Stor Technologies Ltd (NG Credit) collects, uses, and protects your personal data under the Nigeria Data Protection Act 2023.'}
            </Typography>
            <Box sx={{ mt: 3.5, display: 'inline-flex', alignItems: 'center', gap: 1.25, background: alpha('#fff', .07), border: `1px solid ${alpha('#fff', .12)}`, borderRadius: 2, px: 2, py: 1.25 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={alpha('#fff', .5)} strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                </svg>
                <Typography sx={{ fontSize: '.75rem', color: alpha('#fff', .5) }}>
                    Effective March 2026 &nbsp;·&nbsp; Federal Republic of Nigeria
                </Typography>
            </Box>
        </Box>
    </Box>
);

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const Section: FC<{ num: string; title: string; children: ReactNode; delay?: number }> = ({ num, title, children, delay = 0 }) => (
    <Box className={`rv d${delay}`} sx={{ borderBottom: `1px solid ${BRAND.border}`, pb: 6, mb: 6 }}>
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: '.65rem', fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: BRAND.accent, background: BRAND.purpleDark, px: 1.25, py: .5, borderRadius: 1, mt: .75, flexShrink: 0 }}>
                {num}
            </Typography>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h2" sx={{ fontFamily: "'Instrument Serif', serif", fontSize: { xs: '1.2rem', md: '1.6rem' }, fontWeight: 400, color: BRAND.purpleDark, lineHeight: 1.2, mb: 2.25 }}>
                    {title}
                </Typography>
                {children}
            </Box>
        </Box>
    </Box>
);

const P: FC<{ children: ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '.93rem', color: '#3A2F52', lineHeight: 1.8, mb: 1.75 }}>{children}</Typography>
);

const UL: FC<{ items: string[] }> = ({ items }) => (
    <Box component="ul" sx={{ my: 1.75, pl: 0, listStyle: 'none' }}>
        {items.map((item, i) => (
            <Box key={i} component="li" sx={{ fontSize: '.88rem', color: '#3A2F52', lineHeight: 1.75, py: .75, pl: 2.75, position: 'relative' }}>
                <Box sx={{ position: 'absolute', left: 0, top: '14px', width: 7, height: 7, borderRadius: '50%', background: BRAND.accent }} />
                {item}
            </Box>
        ))}
    </Box>
);

const Callout: FC<{ children: ReactNode }> = ({ children }) => (
    <Box sx={{ background: BRAND.lavenderSoft, borderLeft: `3px solid ${BRAND.purple}`, borderRadius: '0 8px 8px 0', px: 2.25, py: 1.75, my: 2.5, fontSize: '.86rem', color: BRAND.purpleDark, lineHeight: 1.7 }}>
        {children}
    </Box>
);

const ContactCard: FC<{ items: { label: string; value: string; href?: string }[] }> = ({ items }) => (
    <Box sx={{ mt: 6.5, background: `linear-gradient(135deg, ${BRAND.purpleDeep} 0%, ${BRAND.purpleDark} 100%)`, borderRadius: 4, p: { xs: 3, md: 4.5 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND.accent, .15)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
        <Typography variant="h3" sx={{ fontFamily: "'Instrument Serif', serif", fontSize: '1.25rem', fontWeight: 400, color: '#fff', mb: .5, gridColumn: '1 / -1' }}>
            Contact & Registered Details
        </Typography>
        {items.map(({ label, value, href }, i) => (
            <Box key={i}>
                <Typography sx={{ fontSize: '.65rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: alpha('#fff', .4), mb: .5 }}>{label}</Typography>
                {href
                    ? <Box component="a" href={href} sx={{ fontSize: '.88rem', color: BRAND.accent, textDecoration: 'none' }}>{value}</Box>
                    : <Typography sx={{ fontSize: '.88rem', color: alpha('#fff', .85) }}>{value}</Typography>}
            </Box>
        ))}
    </Box>
);

// ─── TERMS ────────────────────────────────────────────────────────────────────
const Terms: FC = () => (
    <Box id="legal-terms">
        <Section num="01" title="About NG Credit" delay={1}>
            <P>NG Credit is a consumer and SME credit facilitation platform <strong>wholly owned and operated by Stor Technologies Ltd</strong>, a company duly incorporated under the laws of the Federal Republic of Nigeria.</P>
            <P>NG Credit is not a standalone deposit-taking or lending institution. It operates through formal partnerships with licensed <strong>Microfinance Banks</strong> and <strong>Leasing Companies</strong>, and sources working capital through <strong>bank loan facilities</strong> secured by Stor Technologies Ltd — funding the financial pipelines and working assets that power credit delivery and revenue collation on behalf of its partners.</P>
            <Callout><strong>Regulatory Note:</strong> All credit products accessible via NG Credit are underwritten or originated by licensed financial institutions regulated by the Central Bank of Nigeria (CBN) and/or the Federal Competition and Consumer Protection Commission (FCCPC).</Callout>
        </Section>
        <Section num="02" title="Acceptance of Terms" delay={2}>
            <P>By accessing or using the NG Credit platform — including its website, mobile application, USSD services, and API integrations — you agree to be bound by these Terms and all applicable Nigerian law, including:</P>
            <UL items={['The Banks and Other Financial Institutions Act (BOFIA) 2020', 'The Nigeria Data Protection Act (NDPA) 2023', 'The Consumer Credit Act 2023', 'The Federal Competition and Consumer Protection Act (FCCPC) 2018', 'CBN Guidelines on Digital Lending and Credit Risk Management']} />
            <P>If you do not accept these terms in full, you must discontinue use of the platform immediately.</P>
        </Section>
        <Section num="03" title="Eligibility" delay={1}>
            <UL items={['Nigerian citizen or lawful resident of the Federal Republic of Nigeria', 'At least 18 years of age', 'Valid Bank Verification Number (BVN) registered with the CBN', 'Valid National Identification Number (NIN) or equivalent government-issued ID', 'Holds or is willing to open an account with a partner Microfinance Bank, where applicable', 'Verifiable source of income or business activity']} />
        </Section>
        <Section num="04" title="Credit Facilitation & Partner Institutions" delay={2}>
            <P>NG Credit acts as a technology intermediary and pipeline operator. Credit decisions are made in accordance with the credit policies of our partner Microfinance Banks and Leasing Companies. NG Credit:</P>
            <UL items={['Aggregates and analyses applicant data to generate credit risk profiles', 'Facilitates credit origination, disbursement, and repayment collection on behalf of partner lenders', 'Manages working asset pipelines funded through bank loan facilities secured by Stor Technologies Ltd', 'Earns platform fees and revenue share from partner institutions, not directly from user interest']} />
            <Callout>Loan agreements are entered into between the borrower and the relevant licensed partner institution. NG Credit is not a party to the loan contract but may act as servicer or collection agent under a disclosed arrangement.</Callout>
        </Section>
        <Section num="05" title="Fees, Interest & Charges" delay={1}>
            <P>Interest rates, fees, and charges applicable to any credit product will be disclosed in full at the point of application, in compliance with CBN Consumer Protection Regulations and FCCPC Regulation on the Conduct of Digital Lending. No undisclosed fees will be applied.</P>
            <UL items={['All rates are expressed as annual percentage rates (APR) where required by law', 'Early repayment options and associated rebates will be disclosed per product', 'Penalty charges for default are disclosed in the loan schedule and comply with CBN guidelines', 'Platform service charges, where applicable, are separate from lender interest and will be itemised']} />
        </Section>
        <Section num="06" title="Credit Reporting" delay={2}>
            <P>NG Credit and its partner institutions report credit performance — including repayment history, defaults, and settlements — to licensed Credit Bureaux in Nigeria, including CRC Credit Bureau, First Central Credit Bureau, and CreditRegistry, in accordance with the Credit Reporting Act 2017.</P>
            <P>Consistent repayment positively impacts your credit score. Defaults may negatively affect your ability to access credit from other institutions.</P>
        </Section>
        <Section num="07" title="Prohibited Conduct" delay={1}>
            <UL items={['Providing false, misleading, or fraudulent information during any application process', 'Attempting to circumvent credit assessment or identity verification systems', 'Using credit proceeds for purposes prohibited under Nigerian law, including terrorism financing or money laundering', 'Engaging in conduct that disrupts or harms the platform, its users, or partner institutions', 'Sharing account credentials or allowing unauthorised third-party access to your NG Credit account']} />
        </Section>
        <Section num="08" title="Limitation of Liability" delay={2}>
            <P>Stor Technologies Ltd and NG Credit shall not be liable for losses arising from force majeure events, CBN or regulatory policy changes, third-party system failures outside NG Credit's control, or user-initiated errors in application data.</P>
            <P>Our aggregate liability in any calendar year shall not exceed the total fees paid by you to NG Credit in that year, except where Nigerian consumer protection law mandates otherwise.</P>
        </Section>
        <Section num="09" title="Dispute Resolution" delay={1}>
            <P>In the event of a dispute, users should first contact NG Credit's customer support. If unresolved within 10 business days, complaints may be escalated to:</P>
            <UL items={['Consumer Protection Department of the Central Bank of Nigeria (CBN)', 'Federal Competition and Consumer Protection Commission (FCCPC)', "The relevant partner institution's internal complaints mechanism"]} />
            <P>These Terms are governed by the laws of the Federal Republic of Nigeria. Disputes not resolved amicably shall be referred to arbitration under the Arbitration and Mediation Act 2023, seated in Lagos, Nigeria.</P>
        </Section>
        <Section num="10" title="Amendments" delay={2}>
            <P>Stor Technologies Ltd reserves the right to amend these Terms at any time. Material changes will be communicated to users at least 14 days before taking effect via in-app notification, SMS, or email. Continued use of the platform after that period constitutes acceptance of the revised Terms.</P>
        </Section>
        <ContactCard items={[
            { label: 'Company',         value: 'Stor Technologies Ltd (trading as NG Credit)' },
            { label: 'Jurisdiction',    value: 'Federal Republic of Nigeria' },
            { label: 'Complaints',      value: 'corporate@ng-credit.com', href: 'mailto:corporate@ng-credit.com' },
        ]} />
    </Box>
);

// ─── PRIVACY ──────────────────────────────────────────────────────────────────
const Privacy: FC = () => (
    <Box id="legal-privacy">
        <Section num="01" title="Data Controller" delay={1}>
            <P><strong>Stor Technologies Ltd</strong>, trading as NG Credit, is the Data Controller responsible for personal data collected through the NG Credit platform. We are committed to full compliance with the <strong>Nigeria Data Protection Act (NDPA) 2023</strong> and the regulations of the Nigeria Data Protection Commission (NDPC).</P>
            <P>As a credit facilitation intermediary, we process personal data both as a Data Controller (for platform operations) and as a Data Processor on behalf of partner Microfinance Banks and Leasing Companies for credit origination and repayment processing.</P>
        </Section>
        <Section num="02" title="Data We Collect" delay={2}>
            <UL items={['Identity Data: Full name, date of birth, BVN, NIN, government-issued ID, photograph', 'Contact Data: Phone number, email address, residential address', 'Financial Data: Bank account details, transaction history, salary or income information, existing credit obligations', 'Device & Technical Data: IP address, device identifiers, app usage metadata, geolocation (where consented)', 'Credit Bureau Data: Credit scores and histories from licensed Nigerian credit bureaux', 'Behavioural Data: Repayment behaviour, application history, platform interaction patterns', 'Partner Data: Information shared by partner Microfinance Banks and Leasing Companies for joint credit assessment']} />
        </Section>
        <Section num="03" title="Lawful Basis for Processing" delay={1}>
            <UL items={['Performance of a Contract: To assess, originate, disburse, service, and collect on credit facilities', 'Legal Obligation: KYC/AML compliance, CBN regulatory reporting, credit bureau submissions, tax obligations', 'Legitimate Interest: Fraud detection, platform security, credit risk modelling, product improvement', 'Consent: Marketing communications, optional data enrichment — consent may be withdrawn at any time']} />
        </Section>
        <Section num="04" title="How We Use Your Data" delay={2}>
            <UL items={['Verify your identity and assess your creditworthiness', 'Facilitate loan origination with partner Microfinance Banks and Leasing Companies', 'Manage loan disbursement, repayment scheduling, and revenue collation through our working asset pipeline', 'Report your credit performance to licensed Nigerian Credit Bureaux', 'Comply with Anti-Money Laundering (AML) and Counter-Terrorism Financing (CTF) obligations', 'Detect, prevent, and investigate fraud', 'Provide customer support and resolve disputes', 'Improve our platform, products, and credit models (using anonymised or aggregated data)', 'Send service communications and, where consented, promotional messages']} />
        </Section>
        <Section num="05" title="Data Sharing & Third Parties" delay={1}>
            <UL items={['Partner Microfinance Banks & Leasing Companies: For credit origination, underwriting, and account servicing', 'Bank Partners: Institutions providing loan facilities to Stor Technologies Ltd to fund working asset pipelines, under data processing agreements', 'Licensed Credit Bureaux: CRC, First Central, CreditRegistry — for credit reporting obligations', 'CBN & NDPC: As required by law or regulatory directive', 'Payment Service Providers: For disbursement and repayment processing (NIBSS, licensed processors)', 'KYC/Identity Verification Providers: For BVN and NIN verification via NIBSS and NIMC', 'Legal & Audit Advisors: Bound by professional confidentiality obligations']} />
            <Callout>We do <strong>not</strong> sell your personal data to third parties. We do not share your data with advertising networks or data brokers.</Callout>
        </Section>
        <Section num="06" title="Data Retention" delay={2}>
            <UL items={['Active account data: Duration of your relationship with NG Credit', 'Loan and transaction records: Minimum 6 years from loan closure (CBN and CAMA requirements)', 'KYC/AML records: Minimum 5 years from account closure (MLPA 2022)', 'Credit bureau submissions: Governed by Credit Reporting Act 2017 (typically 7 years for negative records)', 'Consented marketing data: Until consent is withdrawn']} />
        </Section>
        <Section num="07" title="Your Rights Under the NDPA 2023" delay={1}>
            <P>As a data subject, you have the following rights, exercisable by contacting us at <strong>privacy@ng-credit.com</strong>:</P>
            <UL items={['Right to Access: Request a copy of personal data we hold about you', 'Right to Rectification: Correct inaccurate or incomplete data', 'Right to Erasure: Request deletion of data where no overriding legal basis exists', 'Right to Restrict Processing: Limit how we use your data in certain circumstances', 'Right to Data Portability: Receive your data in a structured, machine-readable format', 'Right to Object: Object to processing based on legitimate interest, including profiling', 'Right to Withdraw Consent: Withdraw consent at any time without affecting prior lawful processing', 'Right to Lodge a Complaint: With the Nigeria Data Protection Commission (NDPC)']} />
            <P>We will respond to rights requests within 30 days. Complex requests may take up to 60 days; you will be notified of any extension.</P>
        </Section>
        <Section num="08" title="Data Security" delay={2}>
            <UL items={['256-bit AES encryption for data at rest and TLS 1.3 for data in transit', 'Multi-factor authentication for platform access and administrative systems', 'Role-based access controls limiting data access to authorised personnel only', 'Regular penetration testing and security audits by independent third parties', 'Incident response procedures with NDPC notification within 72 hours for qualifying breaches']} />
        </Section>
        <Section num="09" title="Automated Decision-Making & Profiling" delay={1}>
            <P>NG Credit uses automated credit scoring and risk profiling to assess loan eligibility. These systems use financial, identity, and behavioural data and may result in automated decisions about whether to offer credit.</P>
            <P>You have the right to request human review of any automated credit decision that significantly affects you by contacting our credit review team at <strong>corporate@ng-credit.com</strong>. We will respond within 10 business days.</P>
        </Section>
        <Section num="10" title="Cookies & Tracking" delay={2}>
            <P>Our web platform uses cookies and similar tracking technologies to maintain session security, analyse usage patterns, and improve the platform. You may manage cookie preferences through your browser settings. Essential cookies required for security and functionality cannot be disabled.</P>
            <P>Our mobile application uses device identifiers and app analytics in accordance with this policy. You may opt out of non-essential analytics in app settings.</P>
        </Section>
        <Section num="11" title="Changes to This Policy" delay={1}>
            <P>We may update this Privacy Policy to reflect changes in our processing activities, new regulatory requirements, or business changes. We will notify you of material changes at least 14 days in advance via in-app notice, SMS, or email. The current version is always available on our platform.</P>
        </Section>
        <ContactCard items={[
            { label: 'Data Controller',   value: 'Stor Technologies Ltd (NG Credit)' },
            { label: 'Regulator',         value: 'Nigeria Data Protection Commission (NDPC)' },
            { label: 'Credit Review',     value: 'corporate@ng-credit.com', href: 'mailto:corporate@ng-credit.com' },
        ]} />
    </Box>
);

// ─── FOOTER (identical to Landing.tsx) ───────────────────────────────────────
const Footer: React.FC = () => (
    <Box component="footer" sx={{ background: BRAND.ink, py: 5, px: { xs: 3, md: 6 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Logo size={30} />
            <Typography sx={{ fontFamily: "'Instrument Serif',serif", fontSize: '1.2rem', color: '#fff' }}>NG Credit</Typography>
        </Box>
        <Typography sx={{ color: alpha('#fff', .25), fontSize: '.78rem' }}>© {new Date().getFullYear()} NG Credit. Finance the future.</Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
            {([
                { label: 'Privacy', href: '/legal?tab=privacy'            },
                { label: 'Terms',   href: '/legal?tab=terms'              },
                { label: 'Contact', href: 'mailto:corporate@ng-credit.com' },
            ] as const).map(({ label, href }) => (
                <Box key={label} component="a" href={href} sx={{ color: alpha('#fff', .35), fontSize: '.78rem', textDecoration: 'none', transition: 'color .2s', '&:hover': { color: BRAND.accent } }}>
                    {label}
                </Box>
            ))}
        </Box>
    </Box>
);

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const NGCreditLegal: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('tab') === 'privacy' ? 'privacy' : 'terms';
    });

    useReveal(activeTab);

    return (
        <Box sx={{ fontFamily: "'DM Sans', sans-serif", background: BRAND.cream, minHeight: '100vh' }}>
            <LegalGlobalStyles />
            <Nav activeTab={activeTab} setActiveTab={setActiveTab} />
            <Hero activeTab={activeTab} />

            {/*
                Both panels always mounted — visibility toggled via display
                so IntersectionObserver always has real DOM elements.
            */}
            <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 3, md: 5 }, pt: 8, pb: 12 }}>
                <Box sx={{ display: activeTab === 'terms'   ? 'block' : 'none' }}><Terms   /></Box>
                <Box sx={{ display: activeTab === 'privacy' ? 'block' : 'none' }}><Privacy /></Box>
            </Box>

            <Footer />
        </Box>
    );
};

export default NGCreditLegal;